/**
 * What Brunel is, in its own words.
 *
 * The desk could always answer what the rows said - which worker is busy, what is waiting -
 * and could say nothing at all about itself. Asked what it was, it answered that it had no
 * facts describing any such system, which is true of the rows and useless to the person
 * reading it. It read like somebody pulled off the street, handed a manager's chair and
 * never told what the job was.
 *
 * So the account of itself is a written thing, here, beside the rule about what counts as a
 * question - not in one screen's code, because the iPad app and anything after it need the
 * same answers and two hand-written descriptions of one system will disagree within a month.
 *
 * It is deliberately small and deliberately honest. It describes what the platform IS and
 * what the owner can ASK, and it never claims a capability: what Brunel can do on any given
 * day is what the rows say it is doing, and that is answered from the rows.
 */

/** One thing the owner will meet, and what it means. */
export interface BriefTerm {
  readonly term: string;
  /** Other words the owner might reach for. */
  readonly also: readonly string[];
  readonly meaning: string;
}

/** What Brunel is, said once. */
export const WHAT_BRUNEL_IS =
  'Brunel is where you put AI agents to work and watch them do it. You say what you want done; '
  + 'it becomes a plan of tasks you approve; approved tasks go out to a worker on one of your '
  + 'computers, which runs a real AI tool in a real folder and reports back what it did.';

/** Why it works the way it does, when somebody asks why they have to approve things. */
export const WHY_APPROVAL =
  'Nothing runs until you approve it. That is the point of the plan step: an agent that can '
  + 'start work on its own is an agent you find out about afterwards.';

/** What it is not, so nobody waits for something that is never coming. */
export const WHAT_IT_IS_NOT =
  'I do not approve plans for you and I do not steer the agents while they work - I report '
  + 'what the rows say and turn what you ask for into a plan you decide on.';

export const BRIEF_TERMS: readonly BriefTerm[] = [
  {
    term: 'worker',
    also: ['workers', 'agent', 'agents', 'staff'],
    meaning: 'A seat that holds one task at a time. A worker lives on one of your computers '
      + 'and runs the AI tool installed there. Idle means it is free for the next task.',
  },
  {
    term: 'computer',
    also: ['computers', 'node', 'nodes', 'machine', 'machines'],
    meaning: 'One of your own machines with Brunel installed on it. It reports in every few '
      + 'seconds, says what tools it has and how much of itself is free, and runs the work. '
      + 'Nothing runs anywhere else.',
  },
  {
    term: 'project',
    also: ['projects', 'workspace', 'workspaces'],
    meaning: 'A body of work with its own folder, its own rules and its own history. Every '
      + 'task belongs to one.',
  },
  {
    term: 'plan',
    also: ['plans'],
    meaning: 'What your instruction becomes before anything happens: a list of tasks, in '
      + 'order, waiting for you to approve or reject it.',
  },
  {
    term: 'task',
    also: ['tasks'],
    meaning: 'One piece of work for one worker. It waits, then runs, then reports what it did '
      + 'and which files it changed.',
  },
  {
    term: 'approval',
    also: ['approvals', 'approve', 'waiting on you'],
    meaning: 'A decision only you can make. Work stops at one until you make it.',
  },
  {
    term: 'run',
    also: ['runs', 'running'],
    meaning: 'A task actually being worked on right now, by a named worker, on a named '
      + 'computer. You can watch what the tool is printing while it goes.',
  },
];

/** The kinds of thing worth saying to it, for somebody who has just arrived. */
export const WHAT_TO_SAY: readonly string[] = [
  'Ask about the work: what is running, who is free, what is waiting on you, which computers are in touch.',
  'Ask what something means: what a worker is, what a plan is, what a computer is.',
  'Say what you want done, in your own words, and it becomes a plan for you to approve.',
];

function normalise(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Asking what this is, rather than what it is doing. */
const ABOUT_ITSELF =
  /\b(what (is|are) (this|brunel|you)|what s (this|brunel)|who (are|r) you|what are you|explain (brunel|yourself|this)|how does (this|brunel) work|what does (this|brunel) do|inside brunel|about brunel|your (role|job|purpose)|what is your (role|job|purpose))\b/;

/** Asking why a person has to be in the loop at all. */
const ABOUT_APPROVAL =
  /\b(why (do i|must i|should i) (have to )?approve|why approval|why (does|do) (it|everything) (need|wait for) (my )?approval|why can t you approve)\b/;

/**
 * An answer about Brunel itself, or null when the question is about the work rather than the
 * system. Null is the important half: everything about the work is answered from the rows,
 * and this must never speak over that with a description.
 */
export function answerAboutBrunel(command: string): string | null {
  const asked = normalise(command);
  if (asked === '') return null;

  if (ABOUT_APPROVAL.test(asked)) {
    return `${WHY_APPROVAL} ${WHAT_IT_IS_NOT}`;
  }

  if (ABOUT_ITSELF.test(asked)) {
    return `${WHAT_BRUNEL_IS} ${WHY_APPROVAL} ${WHAT_IT_IS_NOT}`;
  }

  // "what is a worker" and the like: a definition, not a headcount. The question has to ask
  // what the thing IS - "how many workers" and "what is worker 2 doing" are about the rows.
  const defining = /\bwhat (is|are|s) (an?|the)? ?([a-z]+)\b/.exec(asked);
  if (defining !== null) {
    const subject = defining[3] ?? '';
    const term = BRIEF_TERMS.find((entry) => entry.term === subject || entry.also.includes(subject));
    // "what is worker 2 doing" is about that worker, not about the word.
    if (term !== undefined && !/\b\d+\b/.test(asked) && !/\b(doing|working on|running|busy|status)\b/.test(asked)) {
      return term.meaning;
    }
  }

  return null;
}

/** The whole account, for a screen that wants to show it rather than be asked. */
export function brunelBrief(): string {
  return [
    WHAT_BRUNEL_IS,
    WHY_APPROVAL,
    '',
    ...BRIEF_TERMS.map((entry) => `${entry.term}: ${entry.meaning}`),
    '',
    ...WHAT_TO_SAY,
  ].join('\n');
}
